import React, { useCallback, useRef, useState } from 'react';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { FiCheckCircle } from 'react-icons/fi';
import {
  fetchAddressAutocomplete,
  getAddressClientId,
  isAddressProxyEnabled,
} from '../../../../services/addressApi';
import { SMARTY_EMBEDDED_KEY, US_STATES } from '../../services/labConstants';
import { verifyPatientAddress } from '../../services/patientAddress';

function useSmartyAddressClients() {
  return useRef(() => {
    if (!SMARTY_EMBEDDED_KEY) return { autocompleteClient: null, streetClient: null };
    const credentials = new SmartySDK.core.SharedCredentials(SMARTY_EMBEDDED_KEY);
    return {
      autocompleteClient: new SmartySDK.core.ClientBuilder(credentials)
        .withLicenses(['us-autocomplete-pro-cloud'])
        .buildUsAutocompleteProClient(),
      streetClient: new SmartySDK.core.ClientBuilder(credentials).buildUsStreetApiClient(),
    };
  }).current();
}

export default function PatientAddressFields({
  address,
  setAddress,
  addressVerified: externalAddressVerified,
  setAddressVerified: externalSetAddressVerified,
  zip,
  availResult,
  serviceAreaState,
  method,
  onStateChange,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [localAddressVerified, setLocalAddressVerified] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState('');
  const debounceRef = useRef(null);
  const autocompleteAbortRef = useRef(null);
  const smarty = useSmartyAddressClients();

  const addressVerified = externalAddressVerified !== undefined ? externalAddressVerified : localAddressVerified;
  const setAddressVerified = externalSetAddressVerified || setLocalAddressVerified;

  const effectiveServiceAreaState = serviceAreaState || '';
  const stateMismatch = Boolean(
    effectiveServiceAreaState &&
    address?.state &&
    effectiveServiceAreaState.trim().toUpperCase() !== address.state.trim().toUpperCase()
  );
  // ponytail: only warn about zip mismatch when an availability result exists (service area is known)
  const zipMismatch = Boolean(
    availResult &&
    zip &&
    address?.zip &&
    zip.trim() !== address.zip.trim()
  );

  const verifyAddress = useCallback(async (candidate = address) => {
    if (!candidate.street || !candidate.city || !candidate.state || !candidate.zip) return;
    try {
      const result = await verifyPatientAddress(candidate, { streetClient: smarty.streetClient });
      if (result.isValid && result.verified) {
        if (result.normalizedAddress) {
          setAddress(previous => ({
            ...previous,
            ...result.normalizedAddress,
          }));
        }
        setAddressVerified(true);
        setVerifyMessage('Address verified');
      } else {
        setAddressVerified(false);
        setVerifyMessage(result.error || 'Address not recognized. Please check it before continuing.');
      }
    } catch {
      setAddressVerified(false);
      setVerifyMessage('Address verification is temporarily unavailable.');
    }
  }, [address, setAddress, setAddressVerified, smarty.streetClient]);

  const fetchSuggestions = async (street) => {
    if (street.length < 5) return setSuggestions([]);
    const clientId = getAddressClientId();
    if (isAddressProxyEnabled() && clientId) {
      autocompleteAbortRef.current?.abort();
      const controller = new AbortController();
      autocompleteAbortRef.current = controller;
      try {
        const results = await fetchAddressAutocomplete(clientId, street, {
          maxResults: 6,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setSuggestions(results);
          return;
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }
        console.warn('Address proxy autocomplete failed, trying embedded Smarty:', error);
      }
    }
    if (!smarty.autocompleteClient) return setSuggestions([]);
    try {
      const lookup = new SmartySDK.usAutocompletePro.Lookup(street);
      lookup.maxResults = 6;
      const response = await smarty.autocompleteClient.send(lookup);
      setSuggestions(response.result || []);
    } catch {
      setSuggestions([]);
    }
  };

  const handleStreetChange = (street) => {
    setAddress(previous => ({ ...previous, street }));
    setAddressVerified(false);
    setVerifyMessage('');
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(street), 350);
  };

  const handleAptChange = (apt) => {
    setAddress(previous => ({ ...previous, apt }));
    setAddressVerified(false);
    setVerifyMessage('');
  };

  const handleCityChange = (city) => {
    setAddress(previous => ({ ...previous, city }));
    setAddressVerified(false);
    setVerifyMessage('');
  };

  const handleZipChange = (inputZip) => {
    const cleanZip = inputZip.replace(/\D/g, '').slice(0, 5);
    setAddress(previous => ({ ...previous, zip: cleanZip }));
    setAddressVerified(false);
    setVerifyMessage('');
  };

  const applySuggestion = (suggestion) => {
    const nextAddress = {
      street: suggestion.secondary ? `${suggestion.streetLine} ${suggestion.secondary}` : (suggestion.streetLine || ''),
      apt: suggestion.secondary || address.apt || '',
      city: suggestion.city || address.city || '',
      state: suggestion.state || address.state || '',
      zip: suggestion.zipcode || zip || address.zip || '',
    };
    setAddress(nextAddress);
    setAddressVerified(false);
    setVerifyMessage('');
    setSuggestions([]);
    setTimeout(() => verifyAddress(nextAddress), 100);
  };

  const handleAddressStateChange = (newState) => {
    setAddress(previous => ({ ...previous, state: newState }));
    setAddressVerified(false);
    setVerifyMessage('');
  };

  return (
    <div className="lcw-card">
      <div className="lcw-card-body">
        <div className="lcw-section-title">{method === 'testkit' ? 'Shipping address' : 'Patient address'}</div>
        <p className="lcw-section-sub">Required by the lab for your order and requisition.</p>

        {stateMismatch && (
          <div className="lcw-unavail" role="alert" style={{ marginBottom: 16 }}>
            Patient address state ({address.state}) does not match the service area state ({effectiveServiceAreaState}). Please enter an address in {effectiveServiceAreaState} or change your ZIP code above.
          </div>
        )}

        {zipMismatch && !stateMismatch && (
          <div className="lcw-unavail" role="alert" style={{ marginBottom: 16 }}>
            Patient address ZIP ({address.zip}) does not match the service area ZIP ({zip}). Please enter an address in {zip} or change your ZIP code above.
          </div>
        )}

        <div className="lcw-input-group lcw-input-wrap">
          <label className="lcw-label">Street address</label>
          <input className="lcw-input" value={address.street} onChange={event => handleStreetChange(event.target.value)} onBlur={() => setTimeout(() => setSuggestions([]), 150)} placeholder="1240 Mission St" autoComplete="street-address" />
          {suggestions.length > 0 && (
            <div className="lcw-suggestions">
              {suggestions.map((suggestion, index) => (
                <button type="button" key={`${suggestion.streetLine}-${index}`} className="lcw-suggestion" onMouseDown={() => applySuggestion(suggestion)}>
                  {`${suggestion.streetLine || ""}${suggestion.secondary ? ` ${suggestion.secondary}` : ""}, ${suggestion.city || ""}, ${suggestion.state || ""} ${suggestion.zipcode || ""}`.trim()}
                </button>
              ))}
            </div>
          )}
          {verifyMessage && <div className={addressVerified ? 'lcw-verified' : 'lcw-unverified'}>{addressVerified && <FiCheckCircle size={13} />} {verifyMessage}</div>}
        </div>
        <div className="lcw-input-group">
          <label className="lcw-label">Apt, suite, unit <span className="lcw-label-optional">(optional)</span></label>
          <input className="lcw-input" value={address.apt} onChange={event => handleAptChange(event.target.value)} />
        </div>
        <div className="lcw-input-group">
          <label className="lcw-label">City</label>
          <input className="lcw-input" value={address.city} onChange={event => handleCityChange(event.target.value)} />
        </div>
        <div className="lcw-row-2">
          <div className="lcw-input-group">
            <label className="lcw-label">State</label>
            <select className="lcw-input" value={address.state} onChange={event => handleAddressStateChange(event.target.value)}>
              <option value="">Select...</option>
              {US_STATES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="lcw-input-group">
            <label className="lcw-label">ZIP</label>
            <input className="lcw-input" value={address.zip ?? (zip || '')} onChange={event => handleZipChange(event.target.value)} placeholder={zip || "ZIP"} maxLength={5} />
            <span className="lcw-field-note">Change the ZIP above to update the service area.</span>
          </div>
        </div>
        <button type="button" className="lcw-btn-outline" onClick={() => verifyAddress()}>Verify address</button>
      </div>
    </div>
  );
}
